/**
 * Job-and-poll generation for reports that cannot finish inside one request.
 *
 * The ALB terminates any request idle for 300s, and a back-dated group report
 * over 181 accounts is tens of minutes of serial replay - so for group reports
 * the synchronous POST /fetch-data has a ceiling that no generator tuning can
 * lift. This module posts the same body to /generate-async and polls the job
 * until it reaches a terminal state.
 *
 * The RESULT IS THE SAME ENVELOPE /fetch-data returns, so every consumer
 * downstream (buildReportEnvelope, ReportDisplayModal, the export path) is
 * unchanged - the only difference is how it arrived.
 *
 * `sleep` and `now` are injectable so the loop can be tested as the real async
 * sequence it is, without interleaving fake timers and microtask flushes.
 */
import { API_ENDPOINTS } from '../../../config/api';

export const POLL_INTERVAL_MS = 2000;
export const POLL_SLOW_INTERVAL_MS = 5000;
export const POLL_BACKOFF_AFTER_MS = 60000;

/** Terminal job states: the poll loop stops on all three, not just success. */
const TERMINAL = new Set(['done', 'error', 'stalled']);

function authToken() {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
}

/**
 * Poll cadence for a job that has been running `elapsedMs`.
 *
 * Fast while the answer might be seconds away; slower once it clearly is not.
 * A 20-minute report polled every 2s is 600 round trips for no added
 * responsiveness.
 */
export function pollIntervalFor(elapsedMs) {
  return elapsedMs >= POLL_BACKOFF_AFTER_MS ? POLL_SLOW_INTERVAL_MS : POLL_INTERVAL_MS;
}

async function readJson(response) {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(
      `Server returned non-JSON response (${response.status}): ${response.statusText}. ${text.substring(0, 120)}`
    );
  }
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body.detail || `Failed to generate report: ${response.statusText}`);
  }
  return body;
}

const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${authToken()}`,
});

/** Fire-and-forget: reaches the job's AthenaReportClients and stops their queries. */
function postCancel(token) {
  if (!token) return;
  fetch(API_ENDPOINTS.reports.fetchCancel, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ cancel_token: token }),
    keepalive: true,
  }).catch(() => { /* StopQueryExecution is asynchronous server-side */ });
}

/**
 * Generate a report through the async job path and resolve with its envelope.
 *
 * Rejects on an errored job, a stalled job (the task running it was replaced),
 * or a cancel. A stalled job is a rejection and not a retry on purpose: the
 * work is gone, and silently restarting a 20-minute generation the user did not
 * ask for is worse than telling them.
 *
 * @param {object} opts
 * @param {string} [opts.token]       cancel token, also passed to the job so the server can stop Athena
 * @param {function} [opts.onProgress] called with {members_done, members_total} on every poll
 * @param {function} [opts.onStart]   called with (jobId, cancel) once the job exists
 */
export async function runReportJob(reportType, requestParams, opts = {}) {
  const {
    token,
    onProgress,
    onStart,
    sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
    now = () => Date.now(),
  } = opts;

  const started = await fetch(API_ENDPOINTS.reports.generateAsync, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      reportType, params: requestParams, endpoint: {}, cancel_token: token || null,
    }),
  }).then(readJson);

  const jobId = started.job_id;
  let cancelled = false;
  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    postCancel(token);
  };
  if (typeof onStart === 'function') onStart(jobId, cancel);

  const startedAt = now();
  for (;;) {
    if (cancelled) throw new Error('Report generation was cancelled');
    await sleep(pollIntervalFor(now() - startedAt));
    if (cancelled) throw new Error('Report generation was cancelled');

    // A poll fault is fatal rather than retried: the job store is Postgres
    // behind the same ALB as everything else, so a failing poll means the
    // session or the service is broken, not that the answer is coming.
    const job = await fetch(API_ENDPOINTS.reports.job(jobId), {
      headers: { Authorization: `Bearer ${authToken()}` },
    }).then(readJson);

    if (job.progress && typeof onProgress === 'function') onProgress(job.progress);
    if (!TERMINAL.has(job.status)) continue;
    if (job.status === 'done') return job.result;
    throw new Error(job.error || `Report generation ${job.status}`);
  }
}
